import React, { useState } from "react";
import { http, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { CalendarClock, X } from "lucide-react";
import { toast } from "sonner";

/** Reschedule + cancel actions attached to an upcoming visit card. */
export default function VisitActions({ visit, onChange }) {
  return (
    <div className="flex gap-2 mt-3">
      <RescheduleButton visit={visit} onChange={onChange} />
      <CancelVisitButton visit={visit} onChange={onChange} />
    </div>
  );
}

function RescheduleButton({ visit, onChange }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState((visit.scheduled_date || "").slice(0, 10));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!date) { toast.error("Pick a new date"); return; }
    setBusy(true);
    try {
      await http.post(`/appointments/${visit.id}/reschedule`, { new_date: date, reason });
      toast.success(`Rescheduled to ${date}`);
      setOpen(false);
      onChange();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`visit-reschedule-${visit.id}`}><CalendarClock size={13} className="mr-1" /> Reschedule</Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Move this visit</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">New date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid={`visit-new-date-${visit.id}`} />
          </div>
          <div>
            <Label className="text-xs">Reason (optional)</Label>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. midterms week" data-testid={`visit-reason-${visit.id}`} />
          </div>
          <Button className="w-full bg-primary hover:bg-primary/90" disabled={busy} onClick={save} data-testid={`visit-reschedule-save-${visit.id}`}>{busy ? "Saving…" : "Confirm new date"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CancelVisitButton({ visit, onChange }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function cancel() {
    setBusy(true);
    try {
      await http.post(`/appointments/${visit.id}/status`, { status: "cancelled" });
      toast.success("Visit cancelled");
      setOpen(false);
      onChange();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" data-testid={`visit-cancel-${visit.id}`}><X size={13} className="mr-1" /> Cancel</Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Cancel this visit?</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">This one visit will be cancelled. Your subscription and future visits are not affected.</p>
        <Button className="mt-3 w-full bg-red-600 hover:bg-red-700" disabled={busy} onClick={cancel} data-testid={`visit-cancel-confirm-${visit.id}`}>{busy ? "Cancelling…" : "Yes, cancel this visit"}</Button>
      </DialogContent>
    </Dialog>
  );
}
